import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import pagamentosService from "../services/pagamentos";
import {
  UPLOAD_PRESETS,
  validateUploadFile,
} from "../utils/validateUploadFile";
import CharCounter from "./CharCounter";
import ConfirmacaoDigitandoModal from "./ConfirmacaoDigitandoModal";

const BOLETO_PRESET = {
  ...UPLOAD_PRESETS.pagamento,
  allowedMimes: ["application/pdf", "image/png"],
  allowedExtensions: [".pdf", ".png"],
  typeLabel: "PDF ou PNG",
};

const DESCRICAO_MAX = 500;

const AdicionarBoletoModal = ({
  open,
  onClose,
  processoSelecionado,
  onBoletoAdicionado,
}) => {
  const [descricao, setDescricao] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleArquivoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";

    const resultado = await validateUploadFile(file, BOLETO_PRESET);
    if (!resultado.valid) {
      setError(resultado.error);
      setArquivo(null);
      return;
    }
    setError("");
    setArquivo(file);
  };

  const executarEnvio = async () => {
    try {
      setError("");
      setSuccess("");
      setLoading(true);

      await pagamentosService.uploadBoleto(
        processoSelecionado.id,
        arquivo,
        descricao,
      );

      setSuccess("Boleto enviado com sucesso!");
      setConfirmOpen(false);

      if (onBoletoAdicionado) {
        await onBoletoAdicionado();
      }

      setTimeout(() => {
        setDescricao("");
        setArquivo(null);
        onClose();
      }, 1500);
    } catch (err) {
      setConfirmOpen(false);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Erro ao enviar boleto. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEnviar = () => {
    setError("");
    setSuccess("");

    if (!processoSelecionado?.id) {
      setError("Solicitação não identificada. Feche e tente novamente.");
      return;
    }

    if (!arquivo) {
      setError("Selecione o arquivo do boleto em PDF.");
      return;
    }

    setConfirmOpen(true);
  };

  const handleFechar = () => {
    if (!loading) {
      setDescricao("");
      setArquivo(null);
      setError("");
      setSuccess("");
      setConfirmOpen(false);
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleFechar} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Solicitar Pagamento de Boleto</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" component="div">
              Faça o upload do PDF do boleto. O empreendedor será solicitado a
              enviar o comprovante de pagamento.
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Descrição (opcional)"
              placeholder="Ex: Boleto da taxa de licenciamento"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              disabled={loading}
              inputProps={{ maxLength: DESCRICAO_MAX }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CharCounter value={descricao} maxLength={DESCRICAO_MAX} />
                  </InputAdornment>
                ),
              }}
            />

            <Button variant="outlined" component="label" disabled={loading}>
              {arquivo
                ? arquivo.name
                : "Selecionar Arquivo do Boleto (PDF ou PNG)"}
              <input
                type="file"
                accept="application/pdf,image/png"
                hidden
                onChange={handleArquivoChange}
              />
            </Button>
            {arquivo && (
              <Typography variant="caption" color="text.secondary">
                {(arquivo.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ mt: 2, justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={handleFechar} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleEnviar}
            variant="contained"
            color="secondary"
            disabled={loading}
          >
            Enviar Boleto
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmacaoDigitandoModal
        open={confirmOpen}
        onClose={() => !loading && setConfirmOpen(false)}
        onConfirm={executarEnvio}
        tipoDocumento="Boleto"
        loading={loading}
      />
    </>
  );
};

export default AdicionarBoletoModal;
